import { ReactNode, RefObject, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { slugify } from '@open-tender/utils'
import { Heading } from 'components'
import { useSectionSpy, useStickyLayers } from 'hooks/useNestedMenu'
import {
  sendCategoryEvent,
  sendCategoryEventInScrollableMenu,
} from 'utils/helpers'
import {
  getGroupTargetId,
  makeGroupId,
  makeGroupsSignature,
  makeOtherSectionId,
  makeSections,
  makeSubcategoryId,
  NestedGroup,
  NestedSection,
  scrollRowTo,
} from 'utils/nestedMenu'
import {
  CategoryNavScrollButtonView,
  CategoryNavScrollView,
} from '../Category/CategoryNavScroll'
import MenuCategory, { MenuCategoryView } from './MenuCategory'
import { MenuSection } from './MenuTop'
import SubcategoryNav from './SubcategoryNav'

const NestedParentNavView = styled.nav`
  position: sticky;
  z-index: 10;
  width: 100%;
  height: ${(props) => props.theme.layout.navHeight};
  background-color: ${(props) => props.theme.bgColors.dark};
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    height: ${(props) => props.theme.layout.navHeightMobile};
  }
`

const NestedGroupHeader = styled.header`
  position: sticky;
  z-index: 9;
  background-color: ${(props) => props.theme.bgColors.primary};
  border-bottom: ${(props) => props.theme.border.width} solid
    ${(props) => props.theme.border.color};
`

const NestedGroupInner = styled(MenuCategoryView)`
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    margin-top: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
`

const NestedGroupTitle = styled(Heading)`
  display: block;
  margin: 0;
  padding: 1.2rem 0 0.4rem;
  font-size: ${(props) => props.theme.fonts.sizes.xBig};
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    font-size: ${(props) => props.theme.fonts.sizes.big};
  }
`

// The row is the visible label; the name stays available to screen readers.
const NestedSectionTitle = styled.h3`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`

interface NestedParentNavItem {
  id: string
  name: string
  targetId: string
}

const NestedParentNav = ({
  items,
  activeId,
  top,
  navRef,
  onSelect,
}: {
  items: NestedParentNavItem[]
  activeId: string | null
  top: number
  navRef: RefObject<HTMLElement>
  onSelect: (targetId: string) => void
}) => {
  const rowRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const row = rowRef.current
    if (!row || !activeId || row.scrollWidth <= row.clientWidth) return
    const item = Array.from(
      row.querySelectorAll<HTMLElement>('[data-nav-id]')
    ).find((i) => i.dataset.navId === activeId)
    if (item) scrollRowTo(row, Math.max(0, item.offsetLeft - 24))
  }, [activeId])

  return (
    <NestedParentNavView
      ref={navRef}
      style={{ top }}
      aria-label="Menu categories"
    >
      <CategoryNavScrollView ref={rowRef}>
        <ul>
          {items.map((item) => {
            const isActive = item.id === activeId
            return (
              <li key={item.id} data-nav-id={item.id}>
                <CategoryNavScrollButtonView
                  type="button"
                  active={isActive}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => onSelect(item.targetId)}
                >
                  {item.name}
                </CategoryNavScrollButtonView>
              </li>
            )
          })}
        </ul>
      </CategoryNavScrollView>
    </NestedParentNavView>
  )
}

const NestedGroupSections = ({
  group,
  activeId,
  top,
  registerHeader,
  onSelect,
}: {
  group: NestedGroup
  activeId: string | null
  top: number
  registerHeader: (groupId: string, element: HTMLElement | null) => void
  onSelect: (id: string) => void
}) => {
  const { category, children } = group
  const groupId = makeGroupId(category.id)
  const items = children.map((child) => ({
    id: makeSubcategoryId(category.id, child.id),
    name: child.name,
  }))

  return (
    <div id={groupId}>
      <NestedGroupHeader
        style={{ top }}
        ref={(element) => registerHeader(groupId, element)}
      >
        <NestedGroupInner>
          <NestedGroupTitle as="h2">{category.name}</NestedGroupTitle>
          <SubcategoryNav
            label={`${category.name} subcategories`}
            items={items}
            activeId={activeId}
            onSelect={onSelect}
          />
        </NestedGroupInner>
      </NestedGroupHeader>
      {children.map((child, index) => (
        <div key={child.id} id={items[index].id}>
          <NestedSectionTitle>{child.name}</NestedSectionTitle>
          <MenuCategory category={child} hideHeader />
        </div>
      ))}
    </div>
  )
}

// With no row under it, the heading needs its own space above the border.
const FlatGroupTitle = styled(NestedGroupTitle)`
  padding-bottom: 1.2rem;
`

// A category without subcategories keeps its heading sticky too, without a row.
// Its description stays in the existing header, below the sticky title.
const FlatGroupSection = ({
  group: { category },
  top,
}: {
  group: NestedGroup
  top: number
}) => (
  <div id={makeGroupId(category.id)}>
    <NestedGroupHeader style={{ top }}>
      <NestedGroupInner>
        <FlatGroupTitle as="h2">{category.name}</FlatGroupTitle>
      </NestedGroupInner>
    </NestedGroupHeader>
    <MenuCategory category={category} hideTitle />
  </div>
)

interface NestedMenuProps {
  groups: NestedGroup[]
  // Whole-menu page only
  otherSections?: MenuSection[]
  showParentNav?: boolean
  beforeSections?: ReactNode
  contentId?: string
  // The fixed bar under the header when there is no parent navigation
  layerRef?: RefObject<HTMLElement>
}

const NestedMenu = ({
  groups,
  otherSections = [],
  showParentNav = false,
  beforeSections,
  contentId,
  layerRef,
}: NestedMenuProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const navRef = useRef<HTMLElement | null>(null)
  const headersRef = useRef<Record<string, HTMLElement | null>>({})
  const { header, layer } = useStickyLayers(
    showParentNav || !layerRef ? navRef : layerRef
  )
  const stickyTop = header + layer

  const others = otherSections.map((section) => ({
    section,
    id: makeOtherSectionId(slugify(section.name)),
  }))
  const sections = makeSections(
    groups,
    others.map((other) => other.id)
  )
  const signature = `${makeGroupsSignature(groups)}#${others
    .map((other) => other.id)
    .join(',')}#${stickyTop}`

  const getOffset = ({ groupId }: NestedSection, atStart = false) => {
    const groupHeader = headersRef.current[groupId]
    if (atStart || !groupHeader) return stickyTop
    return stickyTop + groupHeader.getBoundingClientRect().height
  }

  const { activeId, activeGroupId, scrollToSection } = useSectionSpy({
    sections,
    signature,
    containerRef,
    getOffset,
  })

  useEffect(() => {
    if (!showParentNav || !activeGroupId) return
    const timeout = setTimeout(() => {
      const group = groups.find(
        (i) => makeGroupId(i.category.id) === activeGroupId
      )
      if (group) {
        sendCategoryEvent(group.category)
        return
      }
      const other = others.find((i) => i.id === activeGroupId)
      if (other) {
        sendCategoryEventInScrollableMenu(
          [],
          [other.section],
          slugify(other.section.name)
        )
      }
    }, 200)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId, showParentNav])

  const registerHeader = (groupId: string, element: HTMLElement | null) => {
    headersRef.current[groupId] = element
  }

  const navItems = [
    ...others.map((other) => ({
      id: other.id,
      name: other.section.name,
      targetId: other.id,
    })),
    ...groups.map((group) => ({
      id: makeGroupId(group.category.id),
      name: group.category.name,
      targetId: getGroupTargetId(group),
    })),
  ]

  return (
    <div>
      {showParentNav && (
        <NestedParentNav
          items={navItems}
          activeId={activeGroupId}
          top={header}
          navRef={navRef}
          onSelect={scrollToSection}
        />
      )}
      {beforeSections}
      {others.map((other) => (
        <div key={other.id} id={other.id}>
          <MenuCategory category={other.section} />
        </div>
      ))}
      <div id={contentId} ref={containerRef}>
        {groups.map((group) =>
          group.nested ? (
            <NestedGroupSections
              key={group.category.id}
              group={group}
              activeId={activeId}
              top={stickyTop}
              registerHeader={registerHeader}
              onSelect={scrollToSection}
            />
          ) : (
            <FlatGroupSection
              key={group.category.id}
              group={group}
              top={stickyTop}
            />
          )
        )}
      </div>
    </div>
  )
}

export default NestedMenu
